(function () {
  'use strict';

  var SELECTOR = '.psh-section';
  var BREAKPOINT_PX = 768;

  function init() {
    var sections = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < sections.length; i++) initSection(sections[i]);
  }

  function initSection(section) {
    if (section.dataset.pshInit === 'true') return;
    section.dataset.pshInit = 'true';

    var slides = Array.prototype.slice.call(section.querySelectorAll('.psh-slide'));
    if (slides.length < 2) {
      section.classList.add('psh-section--stacked');
      return;
    }

    var pinEnabled = section.dataset.pinEnabled === 'true';
    var mobilePin = section.dataset.mobilePin !== 'disabled-stack';
    var scrollPerSlide = parseInt(section.dataset.scrollPerSlide, 10) || 100;
    var holdPerSlide = parseInt(section.dataset.holdPerSlide, 10);
    if (isNaN(holdPerSlide)) holdPerSlide = 50;

    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var isMobile = window.matchMedia('(max-width: ' + (BREAKPOINT_PX - 1) + 'px)').matches;
    var hasGsap = typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';

    var useStack = !pinEnabled || prefersReduced || (isMobile && !mobilePin) || !hasGsap;

    if (useStack) {
      section.classList.add('psh-section--stacked');
      setupScrollArrows(section);
      return;
    }

    section.classList.add('psh-section--pinned');
    section._pshConfig = {
      slides: slides,
      scrollPerSlide: scrollPerSlide,
      holdPerSlide: holdPerSlide
    };

    buildForCurrentMode(section);
    attachResizeHandlers(section);
    setupScrollArrows(section);
  }

  // --- Visible-slide filtering (per-flavor device visibility) -----------------

  function isMobileViewport() {
    return window.matchMedia('(max-width: ' + (BREAKPOINT_PX - 1) + 'px)').matches;
  }

  // Slides visible on the CURRENT device, honoring each slide's
  // data-show-desktop / data-show-mobile (a missing attr counts as visible).
  function getVisibleSlides(section) {
    var all = Array.prototype.slice.call(section.querySelectorAll('.psh-slide'));
    var mobile = isMobileViewport();
    var visible = [];
    for (var i = 0; i < all.length; i++) {
      var attr = mobile
        ? all[i].getAttribute('data-show-mobile')
        : all[i].getAttribute('data-show-desktop');
      if (attr !== 'false') visible.push(all[i]);
    }
    return visible;
  }

  // Signature of what the current build depends on (mode + ordered visible
  // slides) so a resize across 768px rebuilds when either changes.
  function visibleKey(section, visible, mode) {
    var ids = [];
    for (var i = 0; i < visible.length; i++) {
      ids.push(visible[i].getAttribute('data-slide-index'));
    }
    return mode + ':' + ids.join(',');
  }

  // --- Mode selection ---------------------------------------------------------

  function determineMode(section) {
    var isMobile = isMobileViewport();
    var mode = isMobile
      ? (section.dataset.scrollModeMobile || 'snap')
      : (section.dataset.scrollModeDesktop || 'progressive');
    if (mode !== 'snap' && mode !== 'progressive') mode = 'progressive';
    return mode;
  }

  function buildForCurrentMode(section) {
    var cfg = section._pshConfig;
    if (!cfg) return;

    var visible = getVisibleSlides(section);

    // Fewer than 2 visible slides on this device → nothing to wipe between.
    // Drop the pin and stack: one visible slide just shows in normal flow, and
    // zero visible slides collapse the section cleanly (no empty pinned stage).
    if (visible.length < 2) {
      section.classList.remove('psh-section--pinned');
      section.classList.add('psh-section--stacked');
      section.dataset.pshActiveMode = 'stack';
      section._pshVisibleKey = visibleKey(section, visible, 'stack');
      return;
    }

    section.classList.remove('psh-section--stacked');
    section.classList.add('psh-section--pinned');

    var mode = determineMode(section);
    section.dataset.pshActiveMode = mode;
    // Build the timeline / snap over ONLY the visible slides → sequence, count,
    // scroll distance, dwell and arrows all ignore flavors hidden on this device.
    if (mode === 'snap') {
      setupSnap(section, visible);
    } else {
      setupProgressive(section, visible, cfg.scrollPerSlide, cfg.holdPerSlide);
    }
    section._pshVisibleKey = visibleKey(section, visible, mode);
  }

  // Rebuild when crossing 768px changes EITHER the scroll mode OR the set of
  // visible slides (per-flavor visibility). The signature captures both.
  function rebuildIfChanged(section) {
    if (!section._pshConfig) return;
    var visible = getVisibleSlides(section);
    var mode = visible.length < 2 ? 'stack' : determineMode(section);
    var newKey = visibleKey(section, visible, mode);
    if (newKey === section._pshVisibleKey) return;
    teardownActiveMode(section);
    buildForCurrentMode(section);
  }

  function teardownActiveMode(section) {
    if (section._pshTimeline) {
      var st = section._pshTimeline.scrollTrigger;
      if (st) st.kill();
      section._pshTimeline.kill();
      section._pshTimeline = null;
    }
    if (section._pshSnapST) {
      try { section._pshSnapST.kill(); } catch (e) {}
      section._pshSnapST = null;
    }
    if (section._pshSnapHandlers) {
      window.removeEventListener('wheel', section._pshSnapHandlers.onWheel);
      window.removeEventListener('touchstart', section._pshSnapHandlers.onTouchStart);
      window.removeEventListener('touchmove', section._pshSnapHandlers.onTouchMove);
      window.removeEventListener('keydown', section._pshSnapHandlers.onKeyDown);
      section._pshSnapHandlers = null;
    }
    // Reset slide inline styles set by snap mode
    if (window.gsap) {
      var slides = section.querySelectorAll('.psh-slide');
      for (var i = 0; i < slides.length; i++) {
        window.gsap.set(slides[i], { clearProps: 'clipPath,webkitClipPath' });
      }
    }
    delete document.documentElement.dataset.pshPinned;
  }

  // --- Scroll arrows (shared) -------------------------------------------------

  function setupScrollArrows(section) {
    var arrows = section.querySelectorAll('.psh-scroll-arrow');
    if (arrows.length === 0) return;
    for (var i = 0; i < arrows.length; i++) {
      arrows[i].addEventListener('click', function (e) {
        e.preventDefault();
        handleArrowClick(section, e.currentTarget);
      });
    }
  }

  function handleArrowClick(section, arrow) {
    var slide = arrow.closest('.psh-slide');
    if (!slide) return;

    // Position WITHIN the slides visible on this device (not the baked DOM
    // index): hidden flavors are skipped, so the arrow advances to the next
    // VISIBLE slide. Recomputed at click time → always device-correct.
    var visible = getVisibleSlides(section);
    var slideIndex = visible.indexOf(slide);
    if (slideIndex === -1) return;
    var n = visible.length;

    // Snap mode → direct index advance
    if (section.dataset.pshActiveMode === 'snap' && typeof section._pshSnapTo === 'function') {
      if (slideIndex < n - 1) {
        section._pshSnapTo(slideIndex + 1);
      } else {
        var bottom = section.getBoundingClientRect().bottom + window.scrollY + 1;
        scrollSmoothly(bottom);
      }
      return;
    }

    // Progressive mode (or stacked: no timeline → flow-based target)
    var targetPx;
    var tl = section._pshTimeline;

    if (tl && tl.scrollTrigger) {
      var st = tl.scrollTrigger;
      var startPx = st.start;
      var endPx = st.end;
      if (slideIndex < n - 1) {
        var T = section._pshT;
        var H = section._pshH;
        var progress;
        if (typeof T === 'number' && typeof H === 'number') {
          progress = (slideIndex + 1) * (H + T);
        } else {
          progress = (2 * slideIndex + 3) / (2 * n);
        }
        targetPx = startPx + progress * (endPx - startPx);
      } else {
        targetPx = endPx + 1;
      }
    } else {
      if (slideIndex < n - 1) {
        var nextSlide = visible[slideIndex + 1];
        targetPx = window.scrollY + nextSlide.getBoundingClientRect().top;
      } else {
        targetPx = window.scrollY + section.getBoundingClientRect().bottom + 1;
      }
    }

    scrollSmoothly(Math.round(targetPx));
  }

  function scrollSmoothly(top) {
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { window.scrollTo(0, top); return; }
    try { window.scrollTo({ top: top, behavior: 'smooth' }); }
    catch (e) { window.scrollTo(0, top); }
  }

  // --- PROGRESSIVE mode (original scrub timeline) -----------------------------

  function setupProgressive(section, slides, scrollPerSlide, holdPerSlide) {
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.config({ ignoreMobileResize: true });

    var pinwrap = section.querySelector('.psh-pinwrap');
    var n = slides.length;

    var direction = section.dataset.wipeDirection === 'top-to-bottom' ? 'top-to-bottom' : 'bottom-to-top';
    var fromClip = direction === 'top-to-bottom'
      ? 'inset(0% 0% 100% 0%)'
      : 'inset(100% 0% 0% 0%)';
    var toClip = 'inset(0% 0% 0% 0%)';

    gsap.set(slides[0], { clipPath: toClip, webkitClipPath: toClip });
    for (var i = 1; i < n; i++) {
      gsap.set(slides[i], { clipPath: fromClip, webkitClipPath: fromClip });
    }

    var totalScrollPct = (n - 1) * scrollPerSlide + n * holdPerSlide;
    if (totalScrollPct <= 0) totalScrollPct = n * scrollPerSlide;
    var T = scrollPerSlide / totalScrollPct;
    var H = holdPerSlide / totalScrollPct;

    section._pshT = T;
    section._pshH = H;
    section._pshN = n;

    var tl = gsap.timeline({
      defaults: { ease: 'none' },
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: '+=' + totalScrollPct + '%',
        pin: pinwrap,
        pinSpacing: true,
        scrub: 1,
        invalidateOnRefresh: true,
        anticipatePin: 1,
        onToggle: function (self) {
          if (self.isActive) document.documentElement.dataset.pshPinned = 'true';
          else delete document.documentElement.dataset.pshPinned;
        }
      }
    });

    tl.to({}, { duration: 1 }, 0);
    for (var j = 0; j < n - 1; j++) {
      var k = j + 1;
      var startPos = k * H + j * T;
      tl.to(slides[k], { clipPath: toClip, webkitClipPath: toClip, duration: T }, startPos);
    }

    section._pshTimeline = tl;
  }

  // --- SNAP mode (intercept wheel/touch, animate clip + dwell) ----------------

  function setupSnap(section, slides) {
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.config({ ignoreMobileResize: true });

    var pinwrap = section.querySelector('.psh-pinwrap');
    var n = slides.length;
    var dwellMs = parseInt(section.dataset.snapDwellMs, 10);
    if (isNaN(dwellMs)) dwellMs = 800;

    var direction = section.dataset.wipeDirection === 'top-to-bottom' ? 'top-to-bottom' : 'bottom-to-top';
    var fromClip = direction === 'top-to-bottom'
      ? 'inset(0% 0% 100% 0%)'
      : 'inset(100% 0% 0% 0%)';
    var toClip = 'inset(0% 0% 0% 0%)';

    // Initial state: first slide revealed, rest hidden above
    gsap.set(slides[0], { clipPath: toClip, webkitClipPath: toClip });
    for (var i = 1; i < n; i++) {
      gsap.set(slides[i], { clipPath: fromClip, webkitClipPath: fromClip });
    }

    var currentIdx = 0;
    var isAnimating = false;
    var isDwelling = false;
    var dwellTimer = null;

    // Pin the section. Scroll budget = arbitrary positive distance; user wheel
    // events inside the pin range are intercepted, not consumed by scroll.
    var pinDistance = Math.max(n * 100, 300);
    var pinST = ScrollTrigger.create({
      trigger: section,
      start: 'top top',
      end: '+=' + pinDistance + '%',
      pin: pinwrap,
      pinSpacing: true,
      anticipatePin: 1,
      onToggle: function (self) {
        if (self.isActive) document.documentElement.dataset.pshPinned = 'true';
        else delete document.documentElement.dataset.pshPinned;
      }
    });

    function startDwell() {
      isDwelling = true;
      clearTimeout(dwellTimer);
      dwellTimer = setTimeout(function () { isDwelling = false; }, dwellMs);
    }

    function snapTo(idx) {
      if (idx < 0 || idx >= n || idx === currentIdx || isAnimating) return;
      isAnimating = true;
      var tl = gsap.timeline({
        onComplete: function () {
          isAnimating = false;
          startDwell();
        }
      });
      if (idx > currentIdx) {
        for (var i = currentIdx + 1; i <= idx; i++) {
          tl.to(slides[i], {
            clipPath: toClip, webkitClipPath: toClip,
            duration: 0.35, ease: 'power2.out'
          }, 0);
        }
      } else {
        for (var j = currentIdx; j > idx; j--) {
          tl.to(slides[j], {
            clipPath: fromClip, webkitClipPath: fromClip,
            duration: 0.35, ease: 'power2.out'
          }, 0);
        }
      }
      currentIdx = idx;
    }
    section._pshSnapTo = snapTo;

    function tryAdvance(deltaY) {
      if (!pinST.isActive) return false;
      if (isAnimating || isDwelling) return true; // swallow but no action
      if (deltaY > 0) {
        if (currentIdx < n - 1) { snapTo(currentIdx + 1); return true; }
        return false; // at last slide → let scroll exit pin
      }
      if (deltaY < 0) {
        if (currentIdx > 0) { snapTo(currentIdx - 1); return true; }
        return false; // at first slide → let scroll exit pin upward
      }
      return false;
    }

    function onWheel(e) {
      if (!pinST.isActive) return;
      if (isAnimating || isDwelling) { e.preventDefault(); return; }
      var consumed = tryAdvance(e.deltaY);
      if (consumed) e.preventDefault();
    }

    var touchStartY = null;
    function onTouchStart(e) {
      if (!pinST.isActive) return;
      touchStartY = e.touches[0].clientY;
    }
    function onTouchMove(e) {
      if (!pinST.isActive) return;
      if (isAnimating || isDwelling) { e.preventDefault(); return; }
      if (touchStartY === null) return;
      var delta = touchStartY - e.touches[0].clientY;
      if (Math.abs(delta) > 30) {
        var consumed = tryAdvance(delta);
        if (consumed) { e.preventDefault(); touchStartY = null; }
      }
    }
    function onKeyDown(e) {
      if (!pinST.isActive) return;
      var k = e.key;
      if (k !== 'ArrowDown' && k !== 'ArrowUp' && k !== 'PageDown' && k !== 'PageUp' && k !== ' ') return;
      if (isAnimating || isDwelling) { e.preventDefault(); return; }
      var dir = (k === 'ArrowDown' || k === 'PageDown' || k === ' ') ? 1 : -1;
      var consumed = tryAdvance(dir);
      if (consumed) e.preventDefault();
    }

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('keydown', onKeyDown);

    section._pshSnapST = pinST;
    section._pshSnapHandlers = {
      onWheel: onWheel,
      onTouchStart: onTouchStart,
      onTouchMove: onTouchMove,
      onKeyDown: onKeyDown
    };
  }

  // --- Resize -----------------------------------------------------------------

  function attachResizeHandlers(section) {
    if (section.dataset.pshResize === 'true') return;
    section.dataset.pshResize = 'true';

    var handler = debounce(function () {
      rebuildIfChanged(section);
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }, 200);

    if (window.visualViewport) window.visualViewport.addEventListener('resize', handler);
    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);
  }

  function teardown(section) {
    teardownActiveMode(section);
    section.dataset.pshInit = '';
    section.dataset.pshResize = '';
    section.classList.remove('psh-section--pinned', 'psh-section--stacked');
  }

  function debounce(fn, ms) {
    var t;
    return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SELECTOR);
    if (section) teardown(section);
  });

  document.addEventListener('shopify:section:reorder', function () {
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  });
})();
